import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  listEmails, listEmailDomains, getEmail, deleteEmail, setEmailHandled, getAlertSummary,
} from './apiEmails';
import { useToast } from './Toast';
import { useDomain } from './DomainContext';
import ConfirmDialog from './ui/ConfirmDialog';
import Spinner from './ui/Spinner';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import Alert from './ui/Alert';
import EmailList from './emails/EmailList';
import EmailReader from './emails/EmailReader';
import InboxToolbar from './emails/InboxToolbar';
import {
  spamBucket, matchesQuery, loadSeen, saveSeen, unseenCount, FEEDBACK_TYPE, CONTACT_TYPE, FILTER_TYPES,
} from './emailHelpers';

const PER_PAGE = 50;
const WIDE_QUERY = '(min-width: 900px)';

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    try { return window.matchMedia(query).matches; } catch { return true; }
  });
  useEffect(() => {
    let mql;
    try { mql = window.matchMedia(query); } catch { return undefined; }
    const handler = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

/* Unhandled messages of one type, in one domain or all of them (null while
   the summary loads, so the chip shows no count rather than a wrong one). */
const unhandledOf = (summary, type, domainId) => {
  if (!summary) return null;
  const byDomain = summary.by_type_domain?.[type] || {};
  return domainId ? byDomain[domainId] || 0 : summary.by_type?.[type] || 0;
};

const EDITABLE = /^(input|textarea|select)$/i;
const isTypingTarget = (el) =>
  Boolean(el && (EDITABLE.test(el.tagName || '') || el.isContentEditable));

function Emails() {
  const isWide = useMediaQuery(WIDE_QUERY);
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const { selectedDomain } = useDomain() || {};

  const domainId = searchParams.get('domain') || '';
  const query = searchParams.get('q') || '';
  const filter = searchParams.get('filter') || 'all';
  const openId = searchParams.get('open') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domains, setDomains] = useState([]);
  const [searchText, setSearchText] = useState(query);
  const [seenIds, setSeenIds] = useState(() => loadSeen());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // id string or 'bulk'
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [remoteEmail, setRemoteEmail] = useState(null); // ?open=<id> outside the page
  const [alertSummary, setAlertSummary] = useState(null);
  const listRef = useRef(null);
  const defaultedRef = useRef(false);

  /* ── URL helpers ─────────────────────────────────────────────── */
  const patchParams = useCallback((patch, opts) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, opts);
  }, [setSearchParams]);

  /* Default the domain select to the globally selected domain, once, and only
     when the URL says nothing. ?domain=<id> always wins; picking "All domains"
     sticks because we only ever do this on the first render pass. */
  useEffect(() => {
    if (defaultedRef.current) return;
    if (searchParams.has('domain')) { defaultedRef.current = true; return; }
    if (!selectedDomain?.id) return;
    defaultedRef.current = true;
    patchParams({ domain: selectedDomain.id }, { replace: true });
  }, [selectedDomain, searchParams, patchParams]);

  /* ── data ────────────────────────────────────────────────────── */
  useEffect(() => {
    listEmailDomains().then((d) => setDomains(d.domains || [])).catch(() => {});
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEmails({
        domainId: domainId || undefined,
        q: query || undefined,
        type: FILTER_TYPES[filter],
        page,
        perPage: PER_PAGE,
      });
      setEmails(data.emails || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load emails');
      setEmails([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    getAlertSummary().then(setAlertSummary).catch(() => {});
  }, [domainId, query, page, filter]);

  useEffect(() => { reload(); }, [reload]);

  /* Debounce the search box into the URL (the backend does the matching so a
     query spans every page, not just the one on screen). */
  useEffect(() => { setSearchText(query); }, [query]);
  useEffect(() => {
    if (searchText === query) return undefined;
    const t = setTimeout(() => patchParams({ q: searchText, page: null, open: null }), 350);
    return () => clearTimeout(t);
  }, [searchText, query, patchParams]);

  /* ── derived list ────────────────────────────────────────────── */
  const visible = useMemo(() => {
    const rows = (emails || []).filter((e) => matchesQuery(e, searchText));
    const byFilter = filter === 'all' || FILTER_TYPES[filter]
      ? rows
      : rows.filter((e) => spamBucket(e.spam_score) === filter
        || (filter === 'clean' && spamBucket(e.spam_score) === 'unknown'));
    return [...byFilter].sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [emails, searchText, filter]);

  const counts = useMemo(() => {
    const rows = (emails || []).filter((e) => matchesQuery(e, searchText));
    return {
      all: rows.length,
      spam: rows.filter((e) => spamBucket(e.spam_score) === 'spam').length,
      clean: rows.filter((e) => spamBucket(e.spam_score) !== 'spam').length,
      feedback: unhandledOf(alertSummary, FEEDBACK_TYPE, domainId),
      contact: unhandledOf(alertSummary, CONTACT_TYPE, domainId),
    };
  }, [emails, searchText, alertSummary, domainId]);

  const unread = useMemo(() => unseenCount(visible, seenIds), [visible, seenIds]);

  const openEmail = useMemo(() => {
    if (!openId) return null;
    return visible.find((e) => String(e.id) === openId)
      || emails.find((e) => String(e.id) === openId)
      || (remoteEmail && String(remoteEmail.id) === openId ? remoteEmail : null);
  }, [openId, visible, emails, remoteEmail]);

  /* A deep link can point at a message outside the current page/filter. */
  useEffect(() => {
    if (!openId || openEmail || loading) return;
    let cancelled = false;
    getEmail(openId)
      .then((data) => { if (!cancelled) setRemoteEmail(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [openId, openEmail, loading]);

  /* Keep the cursor on the open message, and inside the list. */
  useEffect(() => {
    if (!visible.length) { setCursor(0); return; }
    const idx = openId ? visible.findIndex((e) => String(e.id) === openId) : -1;
    setCursor((prev) => (idx >= 0 ? idx : Math.min(prev, visible.length - 1)));
  }, [visible, openId]);

  /* ── actions ─────────────────────────────────────────────────── */
  const markSeen = useCallback((ids) => {
    setSeenIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(String(id)));
      saveSeen(next);
      return next;
    });
  }, []);

  const open = useCallback((id) => {
    markSeen([id]);
    patchParams({ open: id });
  }, [markSeen, patchParams]);

  const closeReader = useCallback(() => patchParams({ open: null }), [patchParams]);

  const toggleHandled = useCallback(async (email) => {
    try {
      const updated = await setEmailHandled(email.id, !email.handled);
      const swap = (e) => (String(e.id) === String(updated.id) ? { ...e, ...updated } : e);
      setEmails((prev) => prev.map(swap));
      setRemoteEmail((prev) => (prev ? swap(prev) : prev));
      getAlertSummary().then(setAlertSummary).catch(() => {});
      addToast(updated.handled ? 'Marked handled' : 'Moved back to unhandled', 'success');
    } catch {
      addToast('Could not update the message', 'error');
    }
  }, [addToast]);

  const markAllRead = useCallback(() => {
    markSeen(visible.map((e) => e.id));
    addToast('All messages marked as read', 'success');
  }, [visible, markSeen, addToast]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const all = visible.length > 0 && visible.every((e) => prev.has(String(e.id)));
      return all ? new Set() : new Set(visible.map((e) => String(e.id)));
    });
  }, [visible]);

  const runDelete = useCallback(async () => {
    if (busy || !deleteTarget) return;
    setBusy(true);
    const ids = deleteTarget === 'bulk' ? [...selectedIds] : [deleteTarget];
    try {
      await Promise.all(ids.map((id) => deleteEmail(id)));
      addToast(`Deleted ${ids.length} message${ids.length !== 1 ? 's' : ''}`, 'success');
    } catch {
      addToast('Some messages failed to delete', 'error');
    } finally {
      setBusy(false);
      setDeleteTarget(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(String(id)));
        return next;
      });
      if (ids.includes(openId)) patchParams({ open: null });
      reload();
    }
  }, [busy, deleteTarget, selectedIds, openId, addToast, patchParams, reload]);

  /* ── keyboard ────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      if (deleteTarget || isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!visible.length) return;
        e.preventDefault();
        setCursor((prev) => {
          const next = e.key === 'ArrowDown'
            ? Math.min(prev + 1, visible.length - 1)
            : Math.max(prev - 1, 0);
          const row = document.getElementById(`email-row-${visible[next].id}`);
          if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'Enter') {
        const target = visible[cursor];
        if (!target) return;
        e.preventDefault();
        open(String(target.id));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        const target = openId ? { id: openId } : visible[cursor];
        if (!target) return;
        e.preventDefault();
        setDeleteTarget(String(target.id));
      } else if (e.key === 'Escape' && openId) {
        closeReader();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, cursor, openId, deleteTarget, open, closeReader]);

  /* ── render ──────────────────────────────────────────────────── */
  const totalPages = Math.ceil(total / PER_PAGE) || 1;
  const hasFilter = Boolean(query || domainId || filter !== 'all');
  const cursorId = visible[cursor] ? String(visible[cursor].id) : '';
  const activeId = openId;

  const clearFilters = () => setSearchParams({});

  const list = loading ? (
    <Spinner label="Loading messages…" />
  ) : visible.length === 0 ? (
    <EmptyState
      title={hasFilter ? 'No messages match your filters' : 'No messages yet'}
      message={hasFilter
        ? 'Try another domain, search term or filter.'
        : 'Messages sent to your sites will appear here.'}
      action={hasFilter ? (
        <button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>
      ) : null}
    />
  ) : (
    <EmailList
      emails={visible}
      selectedId={activeId}
      cursorId={isWide && !openId ? cursorId : ''}
      seenIds={seenIds}
      selectedIds={selectedIds}
      showDomain={!domainId}
      onOpen={open}
      onToggleSelect={toggleSelect}
      onToggleAll={toggleAll}
      listRef={listRef}
    />
  );

  const reader = (
    <EmailReader
      email={openEmail}
      onBack={isWide ? null : closeReader}
      onDelete={(id) => setDeleteTarget(id)}
      onToggleHandled={toggleHandled}
    />
  );

  return (
    <div className="page">
      <PageHeader
        title="Inbox"
        subtitle={total > 0
          ? [
            hasFilter && visible.length !== total
              ? `${visible.length} of ${total} messages`
              : `${total} message${total !== 1 ? 's' : ''}`,
            unread > 0 ? `${unread} unread${hasFilter ? ' here' : ''}` : null,
          ].filter(Boolean).join(' · ')
          : 'Messages received across your sites'}
        actions={(
          <>
            {unread > 0 && (
              <button type="button" className="btn btn-secondary" onClick={markAllRead}>
                Mark all as read
              </button>
            )}
            {selectedIds.size > 0 && (
              <button type="button" className="btn btn-danger" onClick={() => setDeleteTarget('bulk')}>
                Delete {selectedIds.size} selected
              </button>
            )}
          </>
        )}
      />

      <InboxToolbar
        domainId={domainId}
        domains={domains}
        search={searchText}
        filter={filter}
        counts={counts}
        onDomainChange={(value) => patchParams({ domain: value, page: null, open: null })}
        onSearchChange={setSearchText}
        onFilterChange={(value) => patchParams({ filter: value === 'all' ? null : value, open: null })}
      />

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {isWide ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 380px) 1fr', gap: '16px', alignItems: 'start' }}>
          {list}
          {reader}
        </div>
      ) : openId && openEmail ? (
        reader
      ) : (
        list
      )}

      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => patchParams({ page: page - 1, open: null })}
          >
            Previous
          </button>
          <span className="muted small">Page {page} of {totalPages}</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => patchParams({ page: page + 1, open: null })}
          >
            Next
          </button>
        </div>
      )}

      <p className="muted small" style={{ marginTop: '16px' }}>
        ↑ / ↓ move · Enter opens · Delete removes. Read state is kept in this browser only.
      </p>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={deleteTarget === 'bulk'
          ? `Delete ${selectedIds.size} message${selectedIds.size !== 1 ? 's' : ''}?`
          : 'Delete message?'}
        message={deleteTarget === 'bulk'
          ? `This permanently deletes ${selectedIds.size} selected message${selectedIds.size !== 1 ? 's' : ''} from the datastore.`
          : 'This permanently deletes the message from the datastore.'}
        confirmLabel={busy ? 'Deleting…' : 'Delete'}
        danger
        onConfirm={runDelete}
        onCancel={() => { if (!busy) setDeleteTarget(null); }}
      />
    </div>
  );
}

export default Emails;
